import { StarIcon } from "@design-system/components/Icons";
import { cn } from "@design-system/utils";
import {
  requiredRule,
  useFormControl,
  type FormControlProps,
} from "@mongez/react-form";
import React from "react";
import { InputDescription, InputError, InputHint, InputLabel } from "./";

type RatingInputProps = FormControlProps & {
  description?: React.ReactNode;
  hint?: React.ReactNode;
  /**
   * Maximum rating value
   * @default 5
   */
  maxRating?: number;
  /**
   * Size of the icons in pixels
   * @default 24
   */
  size?: number;
  /**
   * Whether to allow half ratings
   * @default false
   */
  allowHalf?: boolean;
  /**
   * Color of the filled icons
   * @default "#fbbf24" (Amber-400)
   */
  color?: string;
  /**
   * Custom icon component to use instead of star
   * @default Star
   * @example Heart, ThumbsUp, or any custom React component
   */
  icon?: React.ComponentType<{
    size?: number;
    className?: string;
    style?: React.CSSProperties;
  }>;
};

export function RatingInput({
  maxRating = 5,
  size = 24,
  allowHalf = false,
  color = "#fbbf24",
  icon: Icon = StarIcon as any,
  description,
  hint,
  ...props
}: RatingInputProps) {
  const { value, changeValue, error, id } = useFormControl({
    rules: [requiredRule],
    ...props,
  });

  const [hoverRating, setHoverRating] = React.useState<number | null>(null);

  const handleMouseMove = (
    e: React.MouseEvent<HTMLDivElement>,
    index: number,
  ) => {
    if (!allowHalf) {
      setHoverRating(index + 1);
      return;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const halfPoint = rect.width / 2;

    setHoverRating(x < halfPoint ? index + 0.5 : index + 1);
  };

  const handleMouseLeave = () => {
    setHoverRating(null);
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>, index: number) => {
    if (!allowHalf) {
      changeValue(index + 1);
      return;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const halfPoint = rect.width / 2;

    changeValue(x < halfPoint ? index + 0.5 : index + 1);
  };

  const renderStar = (index: number) => {
    const currentRating = hoverRating !== null ? hoverRating : value || 0;
    const filled = currentRating >= index + 1;
    const halfFilled = allowHalf && currentRating === index + 0.5;

    return (
      <div
        key={index}
        role="radio"
        aria-checked
        aria-hidden
        className="relative cursor-pointer"
        onMouseMove={(e) => handleMouseMove(e, index)}
        onClick={(e) => handleClick(e, index)}
        style={{ width: size, height: size }}
      >
        {/* Background icon (always visible) */}
        <Icon
          size={size}
          className={cn(
            "absolute inset-0 transition-colors duration-200",
            "fill-transparent stroke-current",
            "text-gray-300 hover:text-gray-400 dark:text-gray-600 dark:hover:text-gray-500",
          )}
        />
        {/* Filled icon (full or half) */}
        <Icon
          size={size}
          className={cn(
            "absolute inset-0 transition-all duration-200",
            "fill-current stroke-current",
            filled || halfFilled
              ? "text-amber-400 hover:text-amber-500"
              : "text-transparent",
          )}
          style={{
            color: filled || halfFilled ? color : undefined,
            clipPath: halfFilled ? "inset(0 50% 0 0)" : undefined,
          }}
        />
      </div>
    );
  };

  return (
    <div className="flex w-full flex-col gap-1.5">
      <div className="flex flex-col gap-1">
        {props.label && (
          <InputLabel htmlFor={id} required={props.required}>
            {props.label}
          </InputLabel>
        )}
        {description && <InputDescription>{description}</InputDescription>}
      </div>

      <div
        className="flex gap-1"
        onMouseLeave={handleMouseLeave}
        role="radiogroup"
        aria-hidden
        aria-label={props.label?.toString()}
      >
        {Array.from({ length: maxRating }, (_, i) => renderStar(i))}
      </div>

      <div className="flex flex-col gap-1">
        {hint && !error && <InputHint>{hint}</InputHint>}
        <InputError error={error} />
      </div>
    </div>
  );
}
